import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, completePartnerReset } from '../lib/identityAuth';
import { BrandLogo } from '../components/BrandLogo';
import { PasswordField } from '../components/PasswordField';
import { config } from '../lib/config';

/** Staff-enabled partner password reset via emailed token. */
export function PartnerResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < config.passwordMinLength) {
      setError(`Password must be at least ${config.passwordMinLength} characters.`);
      return;
    }
    setLoading(true);
    try {
      await completePartnerReset(token.trim(), password);
      navigate('/partner/login', {
        replace: true,
        state: { notice: 'Password updated. Sign in with your new password.' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Reset failed');
      } else {
        setError(err instanceof Error ? err.message : 'Reset failed');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>Partner password reset</h1>
        <p className="login-card__copy">
          Sense AI staff enabled a password reset for your partner account. Choose a new password
          below.
        </p>
        <form className="login-form" onSubmit={handleSubmit}>
          {!searchParams.get('token') ? (
            <label>
              <span>Reset token</span>
              <input value={token} onChange={(e) => setToken(e.target.value)} required />
            </label>
          ) : null}
          <PasswordField
            label="New password"
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            required
          />
          <PasswordField
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            required
          />
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={loading || !token.trim()}>
            {loading ? 'Saving…' : 'Update password'}
          </button>
        </form>
        <p className="hint-text">
          <Link to="/partner/login">Back to partner sign in</Link>
        </p>
      </div>
    </div>
  );
}
